import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, ShoppingBag, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { scrollToId } from "@/components/site/SmoothScroll";

const LINKS = [
  ["arsenal", "Arsenal"],
  ["why-battery", "Why Battery"],
  ["ecosystem", "Ecosystem"],
  ["field", "In the Field"],
  ["partner", "Training"],
  ["contact", "Contact"],
];

export function Nav() {
  const { count, setOpen } = useCart();
  const [menu, setMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setMenu(false);
    if (loc.pathname !== "/") {
      nav("/");
      setTimeout(() => scrollToId(id), 400);
    } else scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled || menu ? "bg-black/85 backdrop-blur-md border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className={`mx-auto flex max-w-[1500px] items-center justify-between px-5 md:px-10 transition-[padding] duration-500 ${scrolled ? "py-2" : "py-3 md:py-4"}`}>
        <Link to="/" data-testid="nav-logo" className="group relative flex items-center gap-3 px-3 md:px-4" onClick={() => setMenu(false)}>
          <img
            src="/img/logo-emblem.webp"
            alt="BAMF Battery Activated Multi Force Rescue and Tactical Saws"
            data-testid="nav-logo-image"
            className={`relative w-auto object-contain drop-shadow-[0_6px_16px_rgba(0,0,0,0.7)] transition-[height,transform] duration-500 group-hover:scale-105 ${scrolled && !menu ? "h-14 md:h-16" : "h-24 md:h-32"}`}
          />
        </Link>
        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map(([id, label]) => (
            <button
              key={id}
              data-testid={`nav-${id}-link`}
              onClick={() => go(id)}
              className="group relative font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 hover:text-white transition-colors"
            >
              {label}
              <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-[var(--volt)] transition-transform duration-300 group-hover:scale-x-100" />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button
            data-testid="nav-cart-button"
            onClick={() => setOpen(true)}
            className="relative flex h-11 items-center gap-2 border border-white/15 px-4 font-mono text-xs uppercase tracking-widest hover:border-[var(--volt)] transition-colors"
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <motion.span
                key={count}
                initial={{ scale: 0.4 }}
                animate={{ scale: 1 }}
                data-testid="nav-cart-count"
                className="grid h-5 min-w-5 place-items-center bg-volt px-1 text-[10px] font-bold text-black"
              >
                {count}
              </motion.span>
            )}
          </button>
          <button data-testid="nav-menu-toggle" onClick={() => setMenu((m) => !m)} className="lg:hidden grid h-11 w-11 place-items-center border border-white/15">
            {menu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {menu && (
        <motion.nav initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="lg:hidden border-t border-white/10 px-5 pb-8 pt-4">
          {LINKS.map(([id, label], i) => (
            <button
              key={id}
              data-testid={`mobile-nav-${id}-link`}
              onClick={() => go(id)}
              className="flex w-full items-baseline gap-4 border-b border-white/10 py-4 text-left"
            >
              <span className="font-mono text-xs text-volt">0{i + 1}</span>
              <span className="font-display text-4xl font-extrabold uppercase">{label}</span>
            </button>
          ))}
        </motion.nav>
      )}
    </header>
  );
}
